import React, { useCallback } from 'react';
import {
  ArrowTurnDownIcon,
  Box,
  Checkbox,
  ChevronDownIcon,
  ChevronUpIcon,
  Inscription,
} from '@common-fe/common-fe';

import {
  StyledCheckboxNoMargin,
  StyledChevronButton,
  StyledGroupCard,
} from './SyncUpdatesModal.styles';
import { PropertyChangeItem } from './SyncUpdatesModal.types';
import SyncUpdatesPropertyRow from './SyncUpdatesPropertyRow';

interface Group {
  groupType: string;
  section: string;
  items: PropertyChangeItem[];
}

interface Props {
  group: Group;
  expandedProperties: Set<string>;
  selected: Set<string>;
  onToggleGroupProperties: (groupItems: PropertyChangeItem[]) => void;
  onToggleProperty: (propertyName: string) => void;
}

const SyncUpdatesGroupCard: React.FC<Props> = ({
  group,
  expandedProperties,
  selected,
  onToggleGroupProperties,
  onToggleProperty,
}) => {
  const isGroupPropsExpanded = group.items.every((i) => expandedProperties.has(i.propertyName));
  const hasRootProperty = group.items.some((p) => p.isRoot);

  const handleToggleGroup = useCallback(() => {
    onToggleGroupProperties(group.items);
  }, [onToggleGroupProperties, group.items]);

  const handleToggleFirstItem = useCallback(() => {
    onToggleProperty(group.items[0].propertyName);
  }, [onToggleProperty, group.items]);

  return (
    <StyledGroupCard
      direction="column"
      gap="spacing12"
      pad="spacing12"
      $expanded={isGroupPropsExpanded}
      data-testid={`sync-updates-modal_group-card_${group.section}`}
    >
      <Box
        direction="row"
        align="center"
        data-testid={`sync-updates-modal_group_${group.section}`}
      >
        <Box direction="row" align="center" gap="spacing2">
          <Inscription color="textSecondary" size="small" lineHeight="18px">
            {group.section}
          </Inscription>
          <ArrowTurnDownIcon color="iconSecondary" size="12px" />
        </Box>
      </Box>

      {!isGroupPropsExpanded && group.items[0] && (
        <Box direction="row" align="start" gap="spacing12">
          <StyledChevronButton
            align="center"
            justify="center"
            width="40px"
            height={{ min: '40px', max: '100%' }}
            flex={false}
            data-testid={`sync-updates-modal_group-toggle_${group.section}`}
            onClick={handleToggleGroup}
          >
            <ChevronDownIcon size="16px" color="iconPrimary" />
          </StyledChevronButton>
          <Box flex direction="column" gap="spacing4">
            <Box direction="row" align="center" gap="spacing8">
              <StyledCheckboxNoMargin>
                <Checkbox
                  data-testid={`sync-updates-modal_property-checkbox_${group.items[0].propertyName}`}
                  checked={selected.has(group.items[0].propertyName)}
                  onChange={handleToggleFirstItem}
                />
              </StyledCheckboxNoMargin>
              <Box flex direction="column">
                <Inscription weight="bold" color="textTitle" data-testid={`sync-updates-modal_property-name_${group.items[0].propertyName}`}>
                  {group.items[0].displayName}
                </Inscription>
                {group.items[0].description && (
                  <Inscription size="small" color="textSecondary">
                    {group.items[0].description}
                  </Inscription>
                )}
              </Box>
            </Box>
            {group.items.length > 1 && (
              <Inscription size="small" color="textAccent" data-testid={`sync-updates-modal_collapsed-count_${group.section}`}>
                +{group.items.length - 1} {group.items.length - 1 === 1 ? 'Property' : 'Properties'}
              </Inscription>
            )}
          </Box>
        </Box>
      )}

      {isGroupPropsExpanded && (
        <Box direction="row" align="start" pad={{ top: 'spacing10' }}>
          <StyledChevronButton
            align="center"
            justify="center"
            flex={false}
            data-testid={`sync-updates-modal_group-toggle_${group.section}`}
            onClick={handleToggleGroup}
          >
            <ChevronUpIcon size="16px" color="iconPrimary" />
          </StyledChevronButton>
          <Box flex direction="column">
            {group.items.map((item, index) => (
              <SyncUpdatesPropertyRow
                key={item.propertyName}
                item={item}
                index={index}
                isSelected={selected.has(item.propertyName)}
                isRootSelected={hasRootProperty}
                onToggleProperty={onToggleProperty}
                showCheckbox={!hasRootProperty || item.isRoot}
              />
            ))}
          </Box>
        </Box>
      )}
    </StyledGroupCard>
  );
};

export default SyncUpdatesGroupCard;
